import { Link } from 'react-router-dom';
import { useUpdateCartItem, useRemoveCartItem } from '../../features/cart/useCart.js';
import { cn, formatMoney, imageProps } from '../../lib/format.js';
import { Spinner } from '../ui/Spinner.jsx';

/**
 * One line in the bag. Shared by the slide-over and the cart page so quantity
 * behaviour, stock warnings, and removal are identical in both.
 *
 * The quantity control is capped at what is actually available, so a shopper
 * cannot build a bag the checkout will then reject.
 *
 * A combo line links to the builder rather than a product page, and lists the
 * fragrances picked into it under the bottle they come in.
 */

export const CartLine = ({ line, compact = false }) => {
  const update = useUpdateCartItem();
  const remove = useRemoveCartItem();

  const busy = update.isPending || remove.isPending;
  const atMax = line.quantity >= line.maxQuantity;
  const lowStock = line.stock > 0 && line.stock <= 3;
  const isCombo = line.kind === 'combo';
  const href = isCombo ? '/combos' : `/product/${line.slug}`;
  const picks = line.options?.filter((option) => /^Fragrance d+$/.test(option.name)) ?? [];
  const details = line.options?.filter((option) => !picks.includes(option)) ?? [];

  return (
    <li className={cn('flex gap-3', compact ? 'py-4' : 'py-6')}>
      <Link
        to={href}
        className="shrink-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        {line.image ? (
          <img
            {...imageProps({ url: line.image, alt: line.title }, { sizes: '96px' })}
            className={cn('bg-cream object-cover', compact ? 'h-24 w-20' : 'h-30 w-24')}
          />
        ) : (
          <div className={cn('bg-cream', compact ? 'h-24 w-20' : 'h-30 w-24')} aria-hidden="true" />
        )}
      </Link>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Link
              to={href}
              className="text-xs leading-snug font-medium text-ink transition-colors hover:text-brand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              {line.title}
            </Link>

            {details.length > 0 && (
              <p className="mt-1 text-2xs text-muted">
                {details.map((option) => (option.name === 'Bottle' ? `${option.value} bottle` : option.value)).join(' · ')}
              </p>
            )}

            {isCombo && picks.length > 0 && (
              <ol className="mt-1 list-inside list-decimal text-2xs leading-relaxed text-ink">
                {picks.map((option) => (
                  <li key={option.name}>{option.value}</li>
                ))}
              </ol>
            )}

            <p className="mt-1 text-2xs text-muted" data-numeric>
              {formatMoney(line.unitPrice)} each
            </p>
          </div>

          <p className="shrink-0 text-xs font-medium text-brand" data-numeric>
            {formatMoney(line.lineTotal)}
          </p>
        </div>

        {lowStock && (
          <p className="mt-1.5 text-2xs text-danger">
            Only {line.stock} left — check out soon to keep it.
          </p>
        )}

        <div className="mt-2.5 flex items-center gap-3">
          <div className="flex h-9 items-stretch border border-mist">
            <button
              type="button"
              disabled={busy}
              onClick={() => update.mutate({ itemId: line.id, quantity: line.quantity - 1 })}
              aria-label={line.quantity === 1 ? `Remove ${line.title}` : `Reduce quantity of ${line.title}`}
              className="grid w-8 place-items-center text-ink transition-colors hover:bg-sand disabled:opacity-35 disabled:hover:bg-transparent focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent"
            >
              {line.quantity === 1 ? (
                <svg viewBox="0 0 16 16" className="size-3.5" fill="currentColor" aria-hidden="true">
                  <path d="M6.5 2h3l.5 1H13v1.5H3V3h3zM4 5.5h8l-.6 7.6a1 1 0 0 1-1 .9H5.6a1 1 0 0 1-1-.9z" />
                </svg>
              ) : (
                '−'
              )}
            </button>

            <span className="grid w-9 place-items-center border-x border-mist text-xs text-ink" data-numeric aria-live="polite">
              {busy ? <Spinner className="mx-auto size-3" /> : line.quantity}
            </span>

            <button
              type="button"
              disabled={busy || atMax}
              onClick={() => update.mutate({ itemId: line.id, quantity: line.quantity + 1 })}
              aria-label={`Increase quantity of ${line.title}`}
              title={atMax ? `Only ${line.maxQuantity} available` : undefined}
              className="grid w-8 place-items-center text-ink transition-colors hover:bg-sand disabled:opacity-35 disabled:hover:bg-transparent focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent"
            >
              +
            </button>
          </div>

          <button
            type="button"
            disabled={busy}
            onClick={() => remove.mutate({ itemId: line.id })}
            className="text-2xs text-muted underline decoration-mist underline-offset-4 transition-colors hover:text-danger hover:decoration-danger focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            Remove
          </button>
        </div>
      </div>
    </li>
  );
};

export default CartLine;
